import { useMemo, useState } from "react";
import type { Review } from "../../../../types";
import { formattedDate } from "../../../../utils/formattedData";
import { formatNote } from "../../../../utils/formatNote";
import nl2br from "../../../../utils/nl2br";
import { noteTone, type NoteTone } from "../../../../utils/review";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui";
import SectionHeading from "../SectionHeading";

type SortKey = "recent" | "mieux" | "moins";

const SORT_LABELS: Record<SortKey, string> = {
  recent: "Les plus récents",
  mieux: "Les mieux notés",
  moins: "Les moins bien notés",
};

const REVIEWS_PER_BATCH = 5;

const REVIEW_CHIPS: Record<NoteTone, string> = {
  good: "bg-green-100 text-green-700",
  medium: "bg-amber-100 text-orange-700",
  bad: "bg-red-50 text-red-700",
};

const versionLabel = (review: Review) =>
  [
    review.vehicle_version.brand?.title,
    [review.vehicle_version.modele, review.vehicle_version.sra_commercial]
      .filter(Boolean)
      .join(" "),
  ]
    .filter(Boolean)
    .join(" - ");

const ReviewItem = ({ review, first }: { review: Review; first: boolean }) => {
  const chip = REVIEW_CHIPS[noteTone(review.note)];
  const hasComment = !!review.comment?.trim();
  const hasTops = !!review.tops?.trim();
  const hasFlops = !!review.flops?.trim();

  return (
    <div
      className={`flex flex-col gap-2.5 py-5 ${first ? "" : "border-t border-slate-200"}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={`inline-flex items-baseline gap-0.5 rounded-lg px-3 py-1 ${chip}`}
        >
          <strong className="text-base font-bold">
            {formatNote(review.note)}
          </strong>
          <small className="text-[11px]">/10</small>
        </span>
        <span className="text-[15px] font-semibold">
          {versionLabel(review)}
        </span>
        <span className="text-[13px] text-slate-600">
          {formattedDate(review.created_at)}
        </span>
      </div>
      {hasComment && (
        <p className="m-0 leading-[1.65] font-normal">
          {nl2br(review.comment)}
        </p>
      )}
      {(hasTops || hasFlops) && (
        <div className="flex flex-col gap-1.5">
          {hasTops && (
            <span className="text-sm leading-relaxed">
              <strong className="font-bold text-green-800">
                Points forts (+)
              </strong>{" "}
              {nl2br(review.tops)}
            </span>
          )}
          {hasFlops && (
            <span className="text-sm leading-relaxed">
              <strong className="font-bold text-red-700">Réserves (−)</strong>{" "}
              {nl2br(review.flops)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const Reviews = ({ reviews }: { reviews: Review[] }) => {
  const [sort, setSort] = useState<SortKey>("recent");
  const [visibleCount, setVisibleCount] = useState(REVIEWS_PER_BATCH);

  const sorted = useMemo(() => {
    const copy = [...reviews];
    if (sort === "mieux") return copy.sort((a, b) => b.note - a.note);
    if (sort === "moins") return copy.sort((a, b) => a.note - b.note);
    return copy.sort(
      (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
    );
  }, [reviews, sort]);

  const displayed = sorted.slice(0, visibleCount);
  const remaining = sorted.length - displayed.length;

  return (
    <>
      <SectionHeading
        title="Avis"
        count={reviews.length}
        action={
          reviews.length > 1 && (
            <label className="flex items-center gap-2 text-[13px] text-muted-foreground">
              Trier par
              <Select
                value={sort}
                onValueChange={(value) => setSort(value as SortKey)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={SORT_LABELS[sort]} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SORT_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          )
        }
      />
      {reviews.length === 0 ? (
        <p className="mt-7 text-muted-foreground italic">
          Aucun avis pour ce véhicule pour le moment.
        </p>
      ) : (
        <div className="mt-2  flex flex-col gap-6">
          <div className="flex flex-col">
            {displayed.map((review, index) => (
              <ReviewItem key={review.id} review={review} first={index === 0} />
            ))}
          </div>
          {remaining > 0 && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() =>
                  setVisibleCount((count) => count + REVIEWS_PER_BATCH)
                }
              >
                Voir plus d&apos;avis ({remaining})
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Reviews;
